import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import DocumentCropperModal from '../components/DocumentCropperModal';
import { toast } from 'sonner';
import { 
  MapPin, Clock, LogIn, LogOut, Calendar, 
  Timer, CheckCircle, XCircle, User, Fingerprint,
  CalendarDays, AlertTriangle, Send, History,
  Briefcase, Phone, Mail, Building2, Shield,
  Home, FileText, Settings, Bell, BellDot, Trash2,
  Wallet, Paperclip, Pen, FileDown, Camera, Download, Share2, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABEL_AR = { accountant: 'المحاسب', manager: 'المدير التنفيذي', president: 'الرئيس' };

export default function EmployeePortal() {
  const { username: urlUsername } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loginForm, setLoginForm] = useState({ username: urlUsername || '', password: '' });
  const [loading, setLoading] = useState(false);

  // Quick PIN login (دخول سريع)
  const savedQuickLogin = localStorage.getItem('employee_quick_login');
  const [quickLoginInfo, setQuickLoginInfo] = useState(savedQuickLogin ? JSON.parse(savedQuickLogin) : null);
  const [quickLoginMode, setQuickLoginMode] = useState(!!savedQuickLogin);
  const [pinInput, setPinInput] = useState('');
  const [showSetupPin, setShowSetupPin] = useState(false);
  const [setupPinForm, setSetupPinForm] = useState({ password: '', pin: '', pinConfirm: '' });

  // PWA install (تثبيت التطبيق على الجوال)
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showIosInstallHelp, setShowIosInstallHelp] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [myLeaves, setMyLeaves] = useState([]);
  const [myPermissions, setMyPermissions] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Financial requests
  const [myFinancialRequests, setMyFinancialRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [managedRequests, setManagedRequests] = useState([]);
  const [mySignature, setMySignature] = useState(null);
  const [approverStage, setApproverStage] = useState(null); // 'accountant' | 'manager' | 'president' | null
  const [financialForm, setFinancialForm] = useState({
    title: '', description: '', amount: '', request_type: 'عام',
    attachment_filename: '', attachment_base64: '', attachment_content_type: ''
  });
  const [decisionNotes, setDecisionNotes] = useState({});

  // Custody settlement (تصفية العهدة)
  const now = new Date();
  const [custodyMonth, setCustodyMonth] = useState(now.getMonth() + 1);
  const [custodyYear, setCustodyYear] = useState(now.getFullYear());
  const [custodySettlement, setCustodySettlement] = useState(null);
  const [custodyPendingApprovals, setCustodyPendingApprovals] = useState([]);
  const [custodyItemAmount, setCustodyItemAmount] = useState('');
  const [custodyItemDesc, setCustodyItemDesc] = useState('');
  const [custodyItemPhoto, setCustodyItemPhoto] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState(null);
  const [cropperFilename, setCropperFilename] = useState(null);
  const [custodyDecisionNotes, setCustodyDecisionNotes] = useState({});
  
  // Leave request form
  const [leaveForm, setLeaveForm] = useState({
    leave_type_code: '',
    start_date: '',
    end_date: '',
    days_count: 0,
    reason: ''
  });
  
  // Permission request form
  const [permissionForm, setPermissionForm] = useState({
    permission_date: '',
    start_time: '',
    end_time: '',
    reason: ''
  });

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ==================== تثبيت التطبيق على الجوال (PWA) ====================
  useEffect(() => {
    // نضبط manifest ديناميكيًا بحيث نقطة التشغيل بعد التثبيت تطابق رابط
    // هذه البوابة بالضبط (مهم لو التطبيق يخدم أكثر من منشأة بروابط مختلفة)
    fetch('/manifest.json')
      .then((res) => res.json())
      .then((manifest) => {
        const dynamicManifest = { ...manifest, start_url: `${window.location.pathname}?source=pwa` };
        const blob = new Blob([JSON.stringify(dynamicManifest)], { type: 'application/json' });
        const manifestUrl = URL.createObjectURL(blob);
        let link = document.querySelector('link[rel="manifest"]');
        if (link) link.setAttribute('href', manifestUrl);
      })
      .catch(() => { /* لو فشل التحميل، يبقى المانفست الافتراضي الثابت شغّالًا */ });

    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsStandalone(standalone);

    const alreadyDismissed = localStorage.getItem('pwa_install_dismissed');

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      if (!standalone && !alreadyDismissed) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS Safari لا يدعم beforeinstallprompt إطلاقًا، فنكتشفه يدويًا ونعرض تعليمات
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    if (isIos && !standalone && !alreadyDismissed) {
      setShowInstallBanner(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    if (isIos) {
      setShowIosInstallHelp(true);
      return;
    }
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    await installPromptEvent.userChoice;
    setInstallPromptEvent(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa_install_dismissed', '1');
  };

  useEffect(() => {
    const token = localStorage.getItem('employee_token');
    const emp = localStorage.getItem('employee_data');
    if (token && emp) {
      const empData = JSON.parse(emp);
      setIsLoggedIn(true);
      setEmployee(empData);
      setPermissions(empData.permissions || {});
      loadEmployeeData(empData.employee_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('المتصفح لا يدعم خدمة الموقع');
      return;
    }
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationError('');
      },
      (error) => {
        // نفرّق بين أسباب الخطأ الفعلية بدل رسالة واحدة عامة تضلل الموظف
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError('الموقع غير مفعّل لهذا الموقع بمتصفحك. افتح إعدادات المتصفح/الجهاز واسمح للموقع بالوصول لموقعك، ثم أعد المحاولة.');
        } else if (error.code === error.TIMEOUT) {
          // GPS بطيء (شائع بالأماكن المغلقة) - نجرب مرة ثانية بدقة أقل وأسرع بدل ما نتهم إعدادات الموقع
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setCurrentLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
              setLocationError('');
            },
            () => {
              setLocationError('استغرق تحديد موقعك وقتًا طويلاً. جرّب الخروج لمكان مفتوح أو اضغط "إعادة المحاولة".');
            },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
          );
        } else {
          // POSITION_UNAVAILABLE: إشارة GPS ضعيفة، ليست مشكلة صلاحيات
          setLocationError('تعذر تحديد موقعك حاليًا (إشارة GPS ضعيفة). جرّب الخروج لمكان مفتوح أو اضغط "إعادة المحاولة".');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    requestLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, []);

  useEffect(() => {
    if (leaveForm.start_date && leaveForm.end_date) {
      const start = new Date(leaveForm.start_date);
      const end = new Date(leaveForm.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      setLeaveForm(prev => ({ ...prev, days_count: days > 0 ? days : 0 }));
    }
  }, [leaveForm.start_date, leaveForm.end_date]);

  const loadEmployeeData = async (empCode) => {
    try {
      const [balRes, typesRes, leavesRes, permRes, attRes, notifRes] = await Promise.all([
        api.get(`/hr/leave-balances?employee_code=${empCode}`),
        api.get('/hr/leave-types'),
        api.get(`/hr/leave-requests?employee_code=${empCode}`),
        api.get(`/hr/permission-requests?employee_code=${empCode}`),
        api.get(`/hr/attendance?employee_code=${empCode}`),
        api.get(`/notifications/employee/${empCode}`)
      ]);
      
      setLeaveBalances(balRes.data);
      setLeaveTypes(typesRes.data);
      setMyLeaves(leavesRes.data);
      setMyPermissions(permRes.data);
      setAttendanceHistory(attRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(notifRes.data.filter(n => !n.is_read).length);
      
      const todayDate = new Date();
      const today = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
      const todayAtt = attRes.data.find(a => a.attendance_date === today);
      setTodayAttendance(todayAtt);

      loadFinancialData(empCode);
      loadCustodySettlement(empCode, custodyMonth, custodyYear);
    } catch (error) {
      console.error('Error loading data');
    }
  };

  const loadFinancialData = async (empCode) => {
    try {
      const [myReqRes, settingsRes, sigRes] = await Promise.all([
        api.get(`/financial-requests?employee_code=${empCode}`),
        api.get('/financial-approvals/settings'),
        api.get(`/financial-approvals/signature/${empCode}`),
      ]);
      setMyFinancialRequests(myReqRes.data);
      setMySignature(sigRes.data?.signature_base64 || null);

      const settings = settingsRes.data;
      const stage = ['accountant', 'manager', 'president'].find(
        (s) => settings[`${s}_enabled`] && settings[`${s}_employee_code`] === empCode
      );
      setApproverStage(stage || null);

      if (stage) {
        const [pendingRes, managedRes] = await Promise.all([
          api.get(`/financial-requests?pending_for=${empCode}`),
          api.get(`/financial-requests?decided_by=${empCode}`),
        ]);
        setPendingApprovals(pendingRes.data);
        setManagedRequests(managedRes.data);
      } else {
        setPendingApprovals([]);
        setManagedRequests([]);
      }

      if (stage) {
        try {
          const custodyPendingRes = await api.get(`/custody-settlements?pending_for=${empCode}`);
          setCustodyPendingApprovals(custodyPendingRes.data);
        } catch (e) {
          setCustodyPendingApprovals([]);
        }
      } else {
        setCustodyPendingApprovals([]);
      }
    } catch (error) {
      console.error('Error loading financial data');
    }
  };

  const loadCustodySettlement = async (empCode, month, year) => {
    try {
      const res = await api.post('/custody-settlements/get-or-create', { employee_code: empCode, month, year });
      setCustodySettlement(res.data);
    } catch (error) {
      setCustodySettlement(null);
      if (error.response?.status !== 403) {
        toast.error(error.response?.data?.detail || 'تعذر تحميل تصفية العهدة');
      }
    }
  };

  const handleCustodyPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error('حجم الصورة كبير جدًا (الحد الأقصى 6 ميجابايت)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImageSrc(reader.result);
      setCropperFilename(file.name);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirmed = (croppedDataUrl) => {
    setCustodyItemPhoto({ base64: croppedDataUrl, filename: cropperFilename });
    setCropperOpen(false);
  };

  const handleAddCustodyItem = async () => {
    if (!custodyItemPhoto) {
      toast.error('الرجاء تصوير الفاتورة أولًا');
      return;
    }
    if (!custodyItemAmount || parseFloat(custodyItemAmount) <= 0) {
      toast.error('الرجاء إدخال مبلغ الفاتورة');
      return;
    }
    try {
      await api.post(`/custody-settlements/${custodySettlement.settlement_id}/items`, {
        photo_base64: custodyItemPhoto.base64,
        photo_filename: custodyItemPhoto.filename,
        amount: parseFloat(custodyItemAmount),
        description: custodyItemDesc,
      });
      toast.success('تمت إضافة الفاتورة');
      setCustodyItemPhoto(null);
      setCustodyItemAmount('');
      setCustodyItemDesc('');
      loadCustodySettlement(employee.employee_code, custodyMonth, custodyYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'تعذرت إضافة الفاتورة');
    }
  };

  const handleDeleteCustodyItem = async (itemId) => {
    try {
      await api.delete(`/custody-settlements/${custodySettlement.settlement_id}/items/${itemId}`);
      toast.success('تم حذف الفاتورة');
      loadCustodySettlement(employee.employee_code, custodyMonth, custodyYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'تعذر الحذف');
    }
  };

  const handleLockCustodySettlement = async () => {
    if (!window.confirm('بعد الإقفال لن تتمكن من إضافة أو حذف أي فاتورة إلا إذا رُفضت التصفية. هل أنت متأكد؟')) return;
    try {
      const res = await api.post(`/custody-settlements/${custodySettlement.settlement_id}/lock`);
      setCustodySettlement(res.data);
      toast.success('تم إقفال تصفية العهدة وإرسالها للاعتماد');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'تعذر الإقفال');
    }
  };

  const handleCustodyDecision = async (settlementId, decisionValue) => {
    try {
      await api.post(`/custody-settlements/${settlementId}/decide`, {
        acting_employee_code: employee.employee_code,
        decision: decisionValue,
        notes: custodyDecisionNotes[settlementId] || '',
      });
      toast.success(decisionValue === 'approved' ? 'تم اعتماد تصفية العهدة' : 'تم رفض تصفية العهدة');
      setCustodyDecisionNotes((prev) => ({ ...prev, [settlementId]: '' }));
      loadFinancialData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleSignatureFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('حجم صورة التوقيع كبير جدًا (الحد الأقصى 500 كيلوبايت)');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post('/financial-approvals/signature', {
          employee_code: employee.employee_code,
          signature_base64: reader.result,
        });
        setMySignature(reader.result);
        toast.success('تم حفظ توقيعك بنجاح، وسيظهر تلقائيًا على أي طلب تعتمده');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'تعذر حفظ التوقيع');
      }
    };
    reader.readAsDataURL(file);
  };

  const loadNotifications = async (empCode) => {
    try {
      const res = await api.get(`/notifications/employee/${empCode}`);
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error loading notifications');
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read');
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await api.put(`/notifications/employee/${employee.employee_code}/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('تم تحديد جميع الإشعارات كمقروءة');
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('تم حذف الإشعار');
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await api.post('/hr/employee-login', {
        username: loginForm.username,
        password: loginForm.password
      });
      const empData = {
        employee_code: res.data.employee_code,
        employee_name: res.data.employee_name,
        is_manager: res.data.is_manager,
        permissions: res.data.permissions,
        department: res.data.department,
        job_title: res.data.job_title,
        phone: res.data.phone,
        email: res.data.email
      };
      
      localStorage.setItem('employee_token', res.data.token);
      localStorage.setItem('employee_data', JSON.stringify(empData));
      
      setEmployee(empData);
      setPermissions(res.data.permissions || {});
      setIsLoggedIn(true);
      loadEmployeeData(res.data.employee_code);
      toast.success(`مرحباً ${res.data.employee_name}`);

      // لو ما فعّل الدخول السريع على هذا الجهاز من قبل، اعرض عليه تفعيله
      if (!localStorage.getItem('employee_quick_login')) {
        setShowSetupPin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'بيانات الدخول غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPinSubmit = async (e) => {
    e.preventDefault();
    if (setupPinForm.pin.length < 4 || setupPinForm.pin.length > 6 || !/^\d+$/.test(setupPinForm.pin)) {
      toast.error('الرمز يجب أن يكون من 4 إلى 6 أرقام فقط');
      return;
    }
    if (setupPinForm.pin !== setupPinForm.pinConfirm) {
      toast.error('الرمزان غير متطابقين');
      return;
    }
    try {
      await api.post('/hr/employee-login/set-pin', {
        employee_code: employee.employee_code,
        password: setupPinForm.password,
        pin: setupPinForm.pin,
      });
      const info = { employee_code: employee.employee_code, employee_name: employee.employee_name };
      localStorage.setItem('employee_quick_login', JSON.stringify(info));
      setQuickLoginInfo(info);
      setShowSetupPin(false);
      setSetupPinForm({ password: '', pin: '', pinConfirm: '' });
      toast.success('تم تفعيل الدخول السريع على هذا الجهاز');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'تعذر تفعيل الدخول السريع - تأكد من كلمة المرور');
    }
  };

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    if (pinInput.length < 4) return;
    setLoading(true);
    try {
      const res = await api.post('/hr/employee-login/quick', {
        employee_code: quickLoginInfo.employee_code,
        pin: pinInput,
      });
      const empData = {
        employee_code: res.data.employee_code,
        employee_name: res.data.employee_name,
        is_manager: res.data.is_manager,
        permissions: res.data.permissions,
        department: res.data.department,
        job_title: res.data.job_title,
        phone: res.data.phone,
        email: res.data.email
      };
      localStorage.setItem('employee_token', res.data.token);
      localStorage.setItem('employee_data', JSON.stringify(empData));
      setEmployee(empData);
      setPermissions(res.data.permissions || {});
      setIsLoggedIn(true);
      loadEmployeeData(res.data.employee_code);
      toast.success(`مرحباً ${res.data.employee_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'رمز الدخول السريع غير صحيح');
      setPinInput('');
    } finally {
      setLoading(false);
    }
  };

  const forgetQuickLogin = async () => {
    if (quickLoginInfo) {
      try { await api.delete(`/hr/employee-login/quick-pin/${quickLoginInfo.employee_code}`); } catch (e) { /* ignore */ }
    }
    localStorage.removeItem('employee_quick_login');
    setQuickLoginInfo(null);
    setQuickLoginMode(false);
    setPinInput('');
  };

  const handleLogout = () => {
    localStorage.removeItem('employee_token');
    localStorage.removeItem('employee_data');
    setIsLoggedIn(false);
    setEmployee(null);
    setPermissions({});
  };

  const handleCheckIn = async () => {
    if (!permissions.can_check_in) {
      toast.error('ليس لديك صلاحية البصمة');
      return;
    }
    if (!currentLocation) {
      toast.error('يرجى تفعيل خدمة الموقع');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post(`/hr/attendance/check-in?employee_code=${employee.employee_code}&lat=${currentLocation.lat}&lng=${currentLocation.lng}`);
      toast.success(res.data.message);
      
      if (!res.data.location_valid) {
        toast.warning('تم تسجيل الحضور لكن الموقع خارج نطاق العمل');
      }
      
      loadEmployeeData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!permissions.can_check_in) {
      toast.error('ليس لديك صلاحية البصمة');
      return;
    }
    if (!currentLocation) {
      toast.error('يرجى تفعيل خدمة الموقع');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post(`/hr/attendance/check-out?employee_code=${employee.employee_code}&lat=${currentLocation.lat}&lng=${currentLocation.lng}`);
      toast.success(res.data.message);
      loadEmployeeData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveRequest = async (e) => {
    e.preventDefault();
    if (!permissions.can_request_leave) {
      toast.error('ليس لديك صلاحية طلب الإجازات');
      return;
    }
    try {
      await api.post('/hr/leave-requests', {
        ...leaveForm,
        employee_code: employee.employee_code,
        request_id: `LR-${Date.now()}`
      });
      toast.success('تم تقديم طلب الإجازة بنجاح');
      setLeaveForm({ leave_type_code: '', start_date: '', end_date: '', days_count: 0, reason: '' });
      loadEmployeeData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handlePermissionRequest = async (e) => {
    e.preventDefault();
    if (!permissions.can_request_permission) {
      toast.error('ليس لديك صلاحية طلب الاستئذان');
      return;
    }
    try {
      await api.post('/hr/permission-requests', {
        ...permissionForm,
        employee_code: employee.employee_code,
        request_id: `PR-${Date.now()}`,
        hours_count: 0
      });
      toast.success('تم تقديم طلب الاستئذان بنجاح');
      setPermissionForm({ permission_date: '', start_time: '', end_time: '', reason: '' });
      loadEmployeeData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleFinancialFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('حجم الملف كبير جدًا (الحد الأقصى 8 ميجابايت)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFinancialForm((prev) => ({
        ...prev,
        attachment_filename: file.name,
        attachment_content_type: file.type,
        attachment_base64: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleFinancialRequest = async (e) => {
    e.preventDefault();
    if (!financialForm.title.trim()) {
      toast.error('الرجاء إدخال عنوان الطلب');
      return;
    }
    try {
      await api.post('/financial-requests', {
        ...financialForm,
        amount: parseFloat(financialForm.amount) || 0,
        employee_code: employee.employee_code,
      });
      toast.success('تم تقديم الطلب المالي بنجاح');
      setFinancialForm({ title: '', description: '', amount: '', request_type: 'عام', attachment_filename: '', attachment_base64: '', attachment_content_type: '' });
      loadFinancialData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ أثناء تقديم الطلب');
    }
  };

  const handleFinancialDecision = async (requestId, decisionValue) => {
    try {
      await api.post(`/financial-requests/${requestId}/decide`, {
        acting_employee_code: employee.employee_code,
        decision: decisionValue,
        notes: decisionNotes[requestId] || '',
      });
      toast.success(decisionValue === 'approved' ? 'تم اعتماد الطلب' : 'تم رفض الطلب');
      setDecisionNotes((prev) => ({ ...prev, [requestId]: '' }));
      loadFinancialData(employee.employee_code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const getFinancialStatusBadge = (status) => {
    const map = {
      pending_accountant: { label: 'بانتظار المحاسب', cls: 'bg-yellow-500' },
      pending_manager: { label: 'بانتظار المدير', cls: 'bg-yellow-500' },
      pending_president: { label: 'بانتظار الرئيس', cls: 'bg-yellow-500' },
      approved: { label: 'معتمد', cls: 'bg-green-500' },
      rejected: { label: 'مرفوض', cls: 'bg-red-500' },
    };
    const info = map[status] || { label: status, cls: 'bg-gray-400' };
    return <Badge className={info.cls}>{info.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500">في الانتظار</Badge>;
      case 'approved':
        return <Badge className="bg-green-500">موافق عليه</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">مرفوض</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getLeaveTypeName = (code) => {
    const lt = leaveTypes.find(l => l.leave_type_code === code);
    return lt?.leave_type_name_ar || code;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('ar-SA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-emerald-900/20 opacity-50"></div>
        
        <Card className="w-full max-w-md p-8 relative z-10 bg-white/95 backdrop-blur shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Fingerprint className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">بوابة الموظفين</h1>
            <p className="text-gray-500 text-sm mt-2">
              {quickLoginMode && quickLoginInfo ? `مرحبًا بعودتك، ${quickLoginInfo.employee_name}` : 'سجل دخولك للبصمة وإدارة طلباتك'}
            </p>
          </div>

          {quickLoginMode && quickLoginInfo ? (
            <form onSubmit={handleQuickLogin} className="space-y-5">
              <div>
                <Label className="text-gray-700 text-center block mb-2">أدخل رمز الدخول السريع</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  className="h-14 text-center text-2xl tracking-[0.5em]"
                  autoFocus
                  data-testid="quick-pin-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg font-semibold shadow-lg"
                disabled={loading || pinInput.length < 4}
              >
                {loading ? 'جاري الدخول...' : 'دخول'}
              </Button>
              <div className="flex justify-between text-sm">
                <button type="button" className="text-emerald-700 hover:underline" onClick={() => setQuickLoginMode(false)}>
                  استخدام كلمة المرور الكاملة
                </button>
                <button type="button" className="text-red-500 hover:underline" onClick={forgetQuickLogin}>
                  نسيان هذا الجهاز
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label className="text-gray-700">اسم المستخدم</Label>
              <div className="relative mt-1">
                <User className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  placeholder="أدخل اسم المستخدم"
                  className="ps-10 h-12"
                  required
                  data-testid="employee-username-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-700">كلمة المرور</Label>
              <div className="relative mt-1">
                <Shield className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="أدخل كلمة المرور"
                  className="ps-10 h-12"
                  required
                  data-testid="employee-password-input"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg font-semibold shadow-lg" 
              disabled={loading}
              data-testid="employee-login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span> جاري الدخول...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" /> تسجيل الدخول
                </span>
              )}
            </Button>
            {quickLoginInfo && (
              <button type="button" className="w-full text-center text-sm text-emerald-700 hover:underline" onClick={() => setQuickLoginMode(true)}>
                الرجوع للدخول السريع
              </button>
            )}
          </form>
          )}
        </Card>

        {/* Setup Quick Login Dialog - shown once after first full login on a new device */}
        <Dialog open={showSetupPin} onOpenChange={setShowSetupPin}>
          <DialogContent className="max-w-sm" dir="rtl">
            <DialogHeader>
              <DialogTitle>تفعيل الدخول السريع</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500">
              حدد رمزًا سريًا من 4 إلى 6 أرقام لتسجيل الدخول السريع من هذا الجهاز بدل كتابة كلمة المرور الكاملة كل مرة.
            </p>
            <form onSubmit={handleSetupPinSubmit} className="space-y-3">
              <div>
                <Label>كلمة المرور الحالية (للتأكيد)</Label>
                <Input
                  type="password"
                  value={setupPinForm.password}
                  onChange={(e) => setSetupPinForm({ ...setupPinForm, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>الرمز السريع الجديد (4-6 أرقام)</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={setupPinForm.pin}
                  onChange={(e) => setSetupPinForm({ ...setupPinForm, pin: e.target.value.replace(/\D/g, '') })}
                  required
                />
              </div>
              <div>
                <Label>تأكيد الرمز</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={setupPinForm.pinConfirm}
                  onChange={(e) => setSetupPinForm({ ...setupPinForm, pinConfirm: e.target.value.replace(/\D/g, '') })}
                  required
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowSetupPin(false)}>
                  لاحقًا
                </Button>
                <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  تفعيل
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Portal
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
                <User className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-lg">{employee?.employee_name}</p>
                <p className="text-emerald-100 text-sm">{employee?.job_title || employee?.department || employee?.employee_code}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-end hidden sm:block">
                <p className="text-2xl font-mono font-bold">{formatTime(currentTime)}</p>
                <p className="text-emerald-100 text-xs">{formatDate(currentTime)}</p>
              </div>
              <Button variant="ghost" className="text-white hover:bg-white/20" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Install App Banner */}
      {showInstallBanner && !isStandalone && (
        <div className="bg-gradient-to-l from-emerald-600 via-teal-600 to-emerald-700 text-white">
          <div className="container mx-auto px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
              <Download className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">ثبّت التطبيق على جوالك</p>
              <p className="text-emerald-100 text-xs">وصول أسرع للبصمة والطلبات، بدون فتح المتصفح كل مرة</p>
            </div>
            <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 shrink-0" onClick={handleInstallClick}>
              تثبيت
            </Button>
            <button onClick={dismissInstallBanner} className="text-white/70 hover:text-white shrink-0" aria-label="إغلاق">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* iOS Install Instructions Dialog */}
      <Dialog open={showIosInstallHelp} onOpenChange={setShowIosInstallHelp}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle>تثبيت التطبيق على آيفون</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold shrink-0">1</div>
              <p className="flex items-center gap-1">اضغط زر المشاركة <Share2 className="w-4 h-4 inline" /> بأسفل شاشة Safari</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold shrink-0">2</div>
              <p>اختر "إضافة إلى الشاشة الرئيسية" (Add to Home Screen)</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold shrink-0">3</div>
              <p>اضغط "إضافة" — بيظهر أيقونة التطبيق على شاشتك الرئيسية</p>
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => { setShowIosInstallHelp(false); dismissInstallBanner(); }}>
              فهمت
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid w-full ${permissions.can_custody_settlement ? 'grid-cols-8' : 'grid-cols-7'} h-14 bg-transparent gap-1`}>
              <TabsTrigger value="home" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600">
                <Home className="w-4 h-4" />
                <span className="text-xs">الرئيسية</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600 relative">
                {unreadCount > 0 ? <BellDot className="w-4 h-4 text-red-500" /> : <Bell className="w-4 h-4" />}
                <span className="text-xs">الإشعارات</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="attendance" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600" disabled={!permissions.can_check_in}>
                <Fingerprint className="w-4 h-4" />
                <span className="text-xs">البصمة</span>
              </TabsTrigger>
              <TabsTrigger value="leaves" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600" disabled={!permissions.can_request_leave}>
                <CalendarDays className="w-4 h-4" />
                <span className="text-xs">الإجازات</span>
              </TabsTrigger>
              <TabsTrigger value="permissions" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600" disabled={!permissions.can_request_permission}>
                <Timer className="w-4 h-4" />
                <span className="text-xs">الاستئذان</span>
              </TabsTrigger>
              <TabsTrigger value="financial" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600 relative">
                <Wallet className="w-4 h-4" />
                <span className="text-xs">مالي</span>
                {pendingApprovals.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingApprovals.length > 9 ? '9+' : pendingApprovals.length}
                  </span>
                )}
              </TabsTrigger>
              {permissions.can_custody_settlement && (
                <TabsTrigger value="custody" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600 relative">
                  <Camera className="w-4 h-4" />
                  <span className="text-xs">تصفية عهدة</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="history" className="flex flex-col gap-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600" disabled={!permissions.can_view_attendance}>
                <History className="w-4 h-4" />
                <span className="text-xs">السجل</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="container mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4 mt-0">
            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Bell className="w-5 h-5 text-emerald-600" />
                  الإشعارات
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white">{unreadCount} جديد</Badge>
                  )}
                </h3>
                {notifications.length > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllNotificationsAsRead}>
                    تحديد الكل كمقروء
                  </Button>
                )}
              </div>
              
              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">لا توجد إشعارات</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                        notif.is_read ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
                      }`}
                      onClick={() => !notif.is_read && markNotificationAsRead(notif.notification_id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            notif.notification_type.includes('approved') ? 'bg-green-100' :
                            notif.notification_type.includes('rejected') ? 'bg-red-100' :
                            notif.notification_type === 'salary_issued' ? 'bg-emerald-100' : 'bg-blue-100'
                          }`}>
                            {notif.notification_type === 'salary_issued' && (
                              <span className="text-emerald-600 text-lg">💰</span>
                            )}
                            {notif.notification_type.includes('leave') && (
                              <CalendarDays className={`w-5 h-5 ${
                                notif.notification_type.includes('approved') ? 'text-green-600' : 'text-red-600'
                              }`} />
                            )}
                            {notif.notification_type.includes('permission') && (
                              <Timer className={`w-5 h-5 ${
                                notif.notification_type.includes('approved') ? 'text-green-600' : 'text-red-600'
                              }`} />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{notif.title_ar}</p>
                            <p className="text-sm text-gray-600 mt-1">{notif.message_ar}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(notif.created_at).toLocaleDateString('ar-SA', {
                                year: 'numeric', month: 'long', day: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notif.notification_id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {!notif.is_read && (
                        <div className="mt-2 flex justify-end">
                          <Badge className="bg-blue-500 text-white text-xs">جديد</Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Home Tab */}
          <TabsContent value="home" className="space-y-4 mt-0">
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              {permissions.can_check_in && (
                <Card className="p-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('attendance')}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Fingerprint className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold">البصمة</p>
                      <p className="text-xs text-gray-500">تسجيل حضور/انصراف</p>
                    </div>
                  </div>
                </Card>
              )}
              {permissions.can_request_leave && (
                <Card className="p-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('leaves')}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <CalendarDays className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">طلب إجازة</p>
                      <p className="text-xs text-gray-500">تقديم طلب جديد</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Today's Status */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-600" />
                حالة اليوم
              </h3>
              {todayAttendance ? (
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-gray-500">الحضور</p>
                    <p className="font-mono font-bold text-green-600 text-lg">{todayAttendance.check_in_time || '--:--'}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-xs text-gray-500">الانصراف</p>
                    <p className="font-mono font-bold text-red-600 text-lg">{todayAttendance.check_out_time || '--:--'}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-gray-500">ساعات العمل</p>
                    <p className="font-bold text-blue-600 text-lg">{todayAttendance.work_hours || 0}</p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">لم يتم تسجيل الحضور بعد</p>
              )}
            </Card>

            {/* Leave Balances */}
            {permissions.can_view_balance && leaveBalances.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-emerald-600" />
                  رصيد الإجازات
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {leaveBalances.slice(0, 5).map((bal) => (
                    <div key={bal.leave_type_code} className="p-2 border rounded-lg text-center">
                      <p className="text-[10px] text-gray-500 truncate">{getLeaveTypeName(bal.leave_type_code)}</p>
                      <p className="text-xl font-bold text-emerald-600">{bal.remaining_days}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Recent Requests */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                آخر الطلبات
              </h3>
              <div className="space-y-2">
                {[...myLeaves.slice(0, 3), ...myPermissions.slice(0, 2)].slice(0, 4).map((req, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">
                        {req.leave_type_code ? getLeaveTypeName(req.leave_type_code) : 'استئذان'}
                      </p>
                      <p className="text-xs text-gray-500">{req.start_date || req.permission_date}</p>
                    </div>
                    {getStatusBadge(req.status)}
                  </div>
                ))}
                {myLeaves.length === 0 && myPermissions.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا توجد طلبات</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4 mt-0">
            <Card className="p-6">
              {/* Location Status */}
              <div className="mb-6 p-4 rounded-xl bg-gray-50">
                {locationError ? (
                  <div className="flex items-center gap-3 text-red-500">
                    <AlertTriangle className="w-6 h-6 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium">تنبيه</p>
                      <p className="text-sm">{locationError}</p>
                      <Button size="sm" variant="outline" className="mt-2" onClick={requestLocation}>
                        <MapPin className="w-4 h-4 me-1" /> إعادة المحاولة
                      </Button>
                    </div>
                  </div>
                ) : currentLocation ? (
                  <div className="flex items-center gap-3 text-green-600">
                    <MapPin className="w-6 h-6" />
                    <div>
                      <p className="font-medium">تم تحديد موقعك</p>
                      <p className="text-sm text-gray-500">يمكنك الآن تسجيل البصمة</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-yellow-600">
                    <Clock className="w-6 h-6 animate-pulse" />
                    <div>
                      <p className="font-medium">جاري تحديد الموقع...</p>
                      <p className="text-sm text-gray-500">يرجى الانتظار</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Current Time Display */}
              <div className="text-center mb-6">
                <p className="text-5xl font-mono font-bold text-gray-800">{formatTime(currentTime)}</p>
                <p className="text-gray-500 mt-2">{formatDate(currentTime)}</p>
              </div>

              {/* Today's Status */}
              {todayAttendance && (
                <div className="mb-6 p-4 bg-emerald-50 rounded-xl">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500">وقت الحضور</p>
                      <p className="font-mono font-bold text-lg">{todayAttendance.check_in_time || '--:--'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">وقت الانصراف</p>
                      <p className="font-mono font-bold text-lg">{todayAttendance.check_out_time || '--:--'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">ساعات العمل</p>
                      <p className="font-bold text-lg text-emerald-600">{todayAttendance.work_hours || 0} ساعة</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Check In/Out Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  className="h-20 text-lg font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg"
                  onClick={handleCheckIn}
                  disabled={loading || !currentLocation || (todayAttendance?.check_in_time)}
                  data-testid="check-in-button"
                >
                  <div className="flex flex-col items-center gap-1">
                    <LogIn className="w-8 h-8" />
                    <span>تسجيل حضور</span>
                  </div>
                </Button>
                <Button
                  className="h-20 text-lg font-bold bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg"
                  onClick={handleCheckOut}
                  disabled={loading || !currentLocation || !todayAttendance?.check_in_time || todayAttendance?.check_out_time}
                  data-testid="check-out-button"
                >
                  <div className="flex flex-col items-center gap-1">
                    <LogOut className="w-8 h-8" />
                    <span>تسجيل انصراف</span>
                  </div>
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Leaves Tab */}
          <TabsContent value="leaves" className="space-y-4 mt-0">
            {/* Leave Balances */}
            {permissions.can_view_balance && (
              <Card className="p-4">
                <h3 className="font-semibold mb-3">رصيد الإجازات</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {leaveBalances.map((bal) => (
                    <div key={bal.leave_type_code} className="p-3 border rounded-lg text-center">
                      <p className="text-xs text-gray-500">{getLeaveTypeName(bal.leave_type_code)}</p>
                      <p className="text-2xl font-bold text-emerald-600">{bal.remaining_days}</p>
                      <p className="text-xs text-gray-400">من {bal.total_days}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Leave Request Form */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                طلب إجازة جديد
              </h3>
              <form onSubmit={handleLeaveRequest} className="space-y-4">
                <div>
                  <Label>نوع الإجازة *</Label>
                  <select
                    value={leaveForm.leave_type_code}
                    onChange={(e) => setLeaveForm({...leaveForm, leave_type_code: e.target.value})}
                    className="w-full h-11 px-3 border rounded-lg bg-white mt-1"
                    required
                  >
                    <option value="">-- اختر النوع --</option>
                    {leaveTypes.map(lt => (
                      <option key={lt.leave_type_code} value={lt.leave_type_code}>
                        {lt.leave_type_name_ar}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>من تاريخ *</Label>
                    <Input
                      type="date"
                      value={leaveForm.start_date}
                      onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>إلى تاريخ *</Label>
                    <Input
                      type="date"
                      value={leaveForm.end_date}
                      onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-sm text-gray-500">عدد الأيام</p>
                  <p className="text-3xl font-bold text-emerald-600">{leaveForm.days_count}</p>
                </div>
                <div>
                  <Label>السبب</Label>
                  <textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                    className="w-full h-24 px-3 py-2 border rounded-lg bg-white resize-none mt-1"
                    placeholder="سبب الإجازة..."
                  />
                </div>
                <Button type="submit" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-5 h-5 me-2" />
                  تقديم الطلب
                </Button>
              </form>
            </Card>

            {/* My Leave Requests */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">طلباتي السابقة</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {myLeaves.map((leave) => (
                  <div key={leave.request_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{getLeaveTypeName(leave.leave_type_code)}</p>
                      <p className="text-xs text-gray-500">{leave.start_date} - {leave.end_date} ({leave.days_count} يوم)</p>
                    </div>
                    {getStatusBadge(leave.status)}
                  </div>
                ))}
                {myLeaves.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا توجد طلبات</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-4 mt-0">
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Timer className="w-5 h-5 text-emerald-600" />
                طلب استئذان جديد
              </h3>
              <form onSubmit={handlePermissionRequest} className="space-y-4">
                <div>
                  <Label>التاريخ *</Label>
                  <Input
                    type="date"
                    value={permissionForm.permission_date}
                    onChange={(e) => setPermissionForm({...permissionForm, permission_date: e.target.value})}
                    className="mt-1"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>من الساعة *</Label>
                    <Input
                      type="time"
                      value={permissionForm.start_time}
                      onChange={(e) => setPermissionForm({...permissionForm, start_time: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>إلى الساعة *</Label>
                    <Input
                      type="time"
                      value={permissionForm.end_time}
                      onChange={(e) => setPermissionForm({...permissionForm, end_time: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>السبب</Label>
                  <textarea
                    value={permissionForm.reason}
                    onChange={(e) => setPermissionForm({...permissionForm, reason: e.target.value})}
                    className="w-full h-24 px-3 py-2 border rounded-lg bg-white resize-none mt-1"
                    placeholder="سبب الاستئذان..."
                  />
                </div>
                <Button type="submit" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-5 h-5 me-2" />
                  تقديم الطلب
                </Button>
              </form>
            </Card>

            {/* My Permission Requests */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">طلباتي السابقة</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {myPermissions.map((perm) => (
                  <div key={perm.request_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{perm.permission_date}</p>
                      <p className="text-xs text-gray-500">{perm.start_time} - {perm.end_time}</p>
                    </div>
                    {getStatusBadge(perm.status)}
                  </div>
                ))}
                {myPermissions.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا توجد طلبات</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Financial Requests Tab */}
          <TabsContent value="financial" className="space-y-4 mt-0">

            {/* My Signature - only relevant for configured approvers */}
            {approverStage && (
              <Card className="p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Pen className="w-5 h-5 text-emerald-600" />
                  توقيعي
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  يظهر هذا التوقيع تلقائيًا على أي طلب مالي تعتمده كـ{STAGE_LABEL_AR[approverStage]}.
                </p>
                <div className="flex items-center gap-4">
                  {mySignature ? (
                    <img src={mySignature} alt="توقيعي" className="h-14 w-32 object-contain border rounded bg-white" />
                  ) : (
                    <div className="h-14 w-32 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                      لا يوجد توقيع
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureFileChange}
                      className="text-sm"
                      data-testid="signature-upload-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">صورة بحد أقصى 500 كيلوبايت</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Pending Approvals - only visible to configured approvers */}
            {approverStage && (
              <Card className="p-4 border-emerald-200">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-emerald-700">
                  <Shield className="w-5 h-5" />
                  بانتظار موافقتك ({STAGE_LABEL_AR[approverStage]})
                </h3>
                <div className="space-y-3">
                  {pendingApprovals.length === 0 && (
                    <p className="text-center text-gray-500 py-4">لا توجد طلبات بانتظار قرارك حاليًا</p>
                  )}
                  {pendingApprovals.map((req) => (
                    <div key={req.request_id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{req.title}</p>
                          <p className="text-xs text-gray-500">{req.employee_name} - {req.request_type}</p>
                        </div>
                        <p className="font-bold text-emerald-700">{Number(req.amount || 0).toLocaleString()} ر.س</p>
                      </div>
                      {req.description && <p className="text-sm text-gray-600">{req.description}</p>}
                      {req.attachment_filename && (
                        <a
                          href={`${BACKEND_URL}/api/financial-requests/${req.request_id}/attachment`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-700 text-sm hover:underline"
                        >
                          <Paperclip className="w-4 h-4" /> عرض الملف المرفق
                        </a>
                      )}
                      <textarea
                        value={decisionNotes[req.request_id] || ''}
                        onChange={(e) => setDecisionNotes((prev) => ({ ...prev, [req.request_id]: e.target.value }))}
                        placeholder="اكتب ملاحظتك (اختياري)..."
                        className="w-full h-16 px-3 py-2 border rounded-lg bg-white resize-none text-sm"
                      />
                      {!mySignature && (
                        <p className="text-xs text-amber-600">
                          ⚠ ما رفعت توقيعك بعد — اعتمادك للطلب بيسجَّل بدون توقيع ظاهر على المستند. ارفع توقيعك بالأعلى أولًا لو تبيه يظهر.
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleFinancialDecision(req.request_id, 'approved')}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        >
                          <CheckCircle className="w-4 h-4 me-2" /> اعتماد
                        </Button>
                        <Button
                          onClick={() => handleFinancialDecision(req.request_id, 'rejected')}
                          variant="destructive"
                          className="flex-1"
                        >
                          <XCircle className="w-4 h-4 me-2" /> رفض
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Requests I've managed - decision history for this approver */}
            {approverStage && (
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <History className="w-5 h-5 text-emerald-600" />
                  طلبات قمت بإدارتها
                </h3>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {managedRequests.length === 0 && (
                    <p className="text-center text-gray-500 py-4">ما اتخذت قرار على أي طلب مالي بعد</p>
                  )}
                  {managedRequests.map((req) => {
                    const myStage = (req.stages || []).find((s) => s.approver_employee_code === employee.employee_code);
                    return (
                      <div key={req.request_id} className="p-3 bg-gray-50 rounded-lg space-y-1">
                        <div className="flex justify-between items-center">
                          <p className="font-medium">{req.title}</p>
                          {myStage?.decision === 'approved' && <Badge className="bg-green-500">اعتمدته</Badge>}
                          {myStage?.decision === 'rejected' && <Badge className="bg-red-500">رفضته</Badge>}
                        </div>
                        <p className="text-xs text-gray-500">
                          {req.employee_name} - {Number(req.amount || 0).toLocaleString()} ر.س - {req.request_type}
                        </p>
                        {myStage?.notes && <p className="text-xs text-gray-500">ملاحظتي: {myStage.notes}</p>}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-400">
                            {myStage?.decided_at && new Date(myStage.decided_at).toLocaleString('ar-SA')}
                          </p>
                          {getFinancialStatusBadge(req.status)}
                        </div>
                        {(req.status === 'approved' || req.status === 'rejected') && (
                          <a
                            href={`${BACKEND_URL}/api/financial-requests/${req.request_id}/approval-document`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-emerald-700 text-xs hover:underline"
                          >
                            <FileDown className="w-3.5 h-3.5" /> تحميل مستند الاعتماد
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* New Financial Request Form */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-600" />
                طلب مالي جديد
              </h3>
              <form onSubmit={handleFinancialRequest} className="space-y-4">
                <div>
                  <Label>عنوان الطلب *</Label>
                  <Input
                    value={financialForm.title}
                    onChange={(e) => setFinancialForm({ ...financialForm, title: e.target.value })}
                    className="mt-1"
                    placeholder="مثال: طلب سلفة راتب"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>النوع</Label>
                    <select
                      value={financialForm.request_type}
                      onChange={(e) => setFinancialForm({ ...financialForm, request_type: e.target.value })}
                      className="w-full h-11 px-3 border rounded-lg bg-white mt-1"
                    >
                      <option value="عام">عام</option>
                      <option value="سلفة">سلفة</option>
                      <option value="مصروف">مصروف</option>
                      <option value="استرداد">استرداد نفقات</option>
                    </select>
                  </div>
                  <div>
                    <Label>المبلغ (ر.س) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={financialForm.amount}
                      onChange={(e) => setFinancialForm({ ...financialForm, amount: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>التفاصيل</Label>
                  <textarea
                    value={financialForm.description}
                    onChange={(e) => setFinancialForm({ ...financialForm, description: e.target.value })}
                    className="w-full h-24 px-3 py-2 border rounded-lg bg-white resize-none mt-1"
                    placeholder="اشرح تفاصيل الطلب..."
                  />
                </div>
                <div>
                  <Label>إرفاق ملف (اختياري)</Label>
                  <input
                    type="file"
                    onChange={handleFinancialFileChange}
                    className="w-full mt-1 text-sm"
                  />
                  {financialForm.attachment_filename && (
                    <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <Paperclip className="w-3 h-3" /> {financialForm.attachment_filename}
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-5 h-5 me-2" />
                  تقديم الطلب
                </Button>
              </form>
            </Card>

            {/* My Financial Requests */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">طلباتي المالية</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {myFinancialRequests.map((req) => (
                  <div key={req.request_id} className="p-3 bg-gray-50 rounded-lg space-y-1">
                    <div className="flex justify-between items-center">
                      <p className="font-medium">{req.title}</p>
                      {getFinancialStatusBadge(req.status)}
                    </div>
                    <p className="text-xs text-gray-500">{Number(req.amount || 0).toLocaleString()} ر.س - {req.request_type}</p>
                    {req.stages && req.stages.filter(s => s.enabled && s.notes).map((s) => (
                      <p key={s.stage} className="text-xs text-gray-500">
                        ملاحظة {STAGE_LABEL_AR[s.stage]}: {s.notes}
                      </p>
                    ))}
                  </div>
                ))}
                {myFinancialRequests.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا توجد طلبات</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Custody Settlement Tab (تصفية العهدة) */}
          {permissions.can_custody_settlement && (
          <TabsContent value="custody" className="space-y-4 mt-0">

            {/* Pending Custody Approvals - for accountant/manager/president */}
            {custodyPendingApprovals.length > 0 && (
              <Card className="p-4 border-emerald-200">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-emerald-700">
                  <Shield className="w-5 h-5" />
                  تصفيات عهدة بانتظار موافقتك
                </h3>
                <div className="space-y-3">
                  {custodyPendingApprovals.map((s) => (
                    <div key={s.settlement_id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{s.employee_name}</p>
                          <p className="text-xs text-gray-500">{['', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][s.month]} {s.year} - {s.items?.length || 0} فاتورة</p>
                        </div>
                        <p className="font-bold text-emerald-700">{Number(s.total_amount || 0).toLocaleString()} ر.س</p>
                      </div>
                      <a
                        href={`${BACKEND_URL}/api/custody-settlements/${s.settlement_id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-emerald-700 text-sm hover:underline"
                      >
                        <FileDown className="w-4 h-4" /> عرض تفاصيل التصفية وفواتيرها (PDF)
                      </a>
                      <textarea
                        value={custodyDecisionNotes[s.settlement_id] || ''}
                        onChange={(e) => setCustodyDecisionNotes((prev) => ({ ...prev, [s.settlement_id]: e.target.value }))}
                        placeholder="اكتب ملاحظتك (اختياري)..."
                        className="w-full h-16 px-3 py-2 border rounded-lg bg-white resize-none text-sm"
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => handleCustodyDecision(s.settlement_id, 'approved')} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle className="w-4 h-4 me-2" /> اعتماد
                        </Button>
                        <Button onClick={() => handleCustodyDecision(s.settlement_id, 'rejected')} variant="destructive" className="flex-1">
                          <XCircle className="w-4 h-4 me-2" /> رفض
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Month selector */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Camera className="w-5 h-5 text-emerald-600" />
                تصفية العهدة
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-2">
                <div>
                  <Label>الشهر</Label>
                  <select
                    value={custodyMonth}
                    onChange={(e) => {
                      const m = Number(e.target.value);
                      setCustodyMonth(m);
                      loadCustodySettlement(employee.employee_code, m, custodyYear);
                    }}
                    className="w-full h-11 px-3 border rounded-lg bg-white mt-1"
                  >
                    {['', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'].slice(1).map((name, idx) => (
                      <option key={idx + 1} value={idx + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>السنة</Label>
                  <Input
                    type="number"
                    value={custodyYear}
                    onChange={(e) => {
                      const y = Number(e.target.value);
                      setCustodyYear(y);
                      loadCustodySettlement(employee.employee_code, custodyMonth, y);
                    }}
                    className="mt-1"
                  />
                </div>
              </div>
            </Card>

            {custodySettlement && (
              <>
                {/* Status banner */}
                <Card className={`p-4 ${custodySettlement.status === 'approved' ? 'border-emerald-300 bg-emerald-50' : custodySettlement.status === 'rejected' ? 'border-red-300 bg-red-50' : custodySettlement.status.startsWith('pending') ? 'border-amber-300 bg-amber-50' : ''}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">
                        {custodySettlement.status === 'draft' && 'مسودة - قابلة للتعديل'}
                        {custodySettlement.status === 'pending_accountant' && 'بانتظار اعتماد المحاسب'}
                        {custodySettlement.status === 'pending_manager' && 'بانتظار اعتماد المدير التنفيذي'}
                        {custodySettlement.status === 'pending_president' && 'بانتظار اعتماد الرئيس'}
                        {custodySettlement.status === 'approved' && 'معتمدة نهائيًا ✓'}
                        {custodySettlement.status === 'rejected' && 'مرفوضة - عدّل الفواتير وأعد الإقفال'}
                      </p>
                      <p className="text-sm text-gray-500">{custodySettlement.items?.length || 0} فاتورة - الإجمالي {Number(custodySettlement.total_amount || 0).toLocaleString()} ر.س</p>
                    </div>
                    {(custodySettlement.status === 'approved') && (
                      <a
                        href={`${BACKEND_URL}/api/custody-settlements/${custodySettlement.settlement_id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-emerald-700 text-sm hover:underline"
                      >
                        <FileDown className="w-4 h-4" /> طباعة PDF
                      </a>
                    )}
                  </div>
                </Card>

                {/* Add item form - only when editable */}
                {(custodySettlement.status === 'draft' || custodySettlement.status === 'rejected') && (
                  <Card className="p-4">
                    <h4 className="font-medium mb-3">إضافة فاتورة</h4>
                    <div className="space-y-3">
                      <div>
                        <Label>صورة الفاتورة *</Label>
                        <input type="file" accept="image/*" capture="environment" onChange={handleCustodyPhotoChange} className="w-full mt-1 text-sm" />
                        {custodyItemPhoto && (
                          <img src={custodyItemPhoto.base64} alt="معاينة" className="mt-2 h-32 rounded-lg border object-contain" />
                        )}
                      </div>
                      <div>
                        <Label>مبلغ الفاتورة (ر.س) *</Label>
                        <Input type="number" step="0.01" value={custodyItemAmount} onChange={(e) => setCustodyItemAmount(e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>وصف (اختياري)</Label>
                        <Input value={custodyItemDesc} onChange={(e) => setCustodyItemDesc(e.target.value)} className="mt-1" placeholder="مثال: وقود، أدوات مكتبية..." />
                      </div>
                      <Button onClick={handleAddCustodyItem} className="w-full bg-emerald-600 hover:bg-emerald-700">
                        <Camera className="w-4 h-4 me-2" /> إضافة الفاتورة
                      </Button>
                    </div>
                  </Card>
                )}

                {/* Items list */}
                <Card className="p-4">
                  <h4 className="font-medium mb-3">الفواتير المضافة ({custodySettlement.items?.length || 0})</h4>
                  <div className="space-y-2">
                    {(custodySettlement.items || []).map((item) => (
                      <div key={item.item_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        <img
                          src={`${BACKEND_URL}/api/custody-settlements/${custodySettlement.settlement_id}/items/${item.item_id}/photo`}
                          alt="فاتورة"
                          className="w-14 h-14 object-cover rounded border"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.description || 'بدون وصف'}</p>
                          <p className="text-sm text-emerald-700 font-bold">{Number(item.amount).toLocaleString()} ر.س</p>
                        </div>
                        {(custodySettlement.status === 'draft' || custodySettlement.status === 'rejected') && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteCustodyItem(item.item_id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {(!custodySettlement.items || custodySettlement.items.length === 0) && (
                      <p className="text-center text-gray-500 py-4">لا توجد فواتير مضافة بعد</p>
                    )}
                  </div>
                </Card>

                {/* Lock button */}
                {(custodySettlement.status === 'draft' || custodySettlement.status === 'rejected') && custodySettlement.items?.length > 0 && (
                  <Button onClick={handleLockCustodySettlement} className="w-full h-12 bg-amber-600 hover:bg-amber-700">
                    <CheckCircle className="w-5 h-5 me-2" />
                    إقفال تصفية العهدة وإرسالها للاعتماد
                  </Button>
                )}
              </>
            )}
          </TabsContent>
          )}

          <DocumentCropperModal
            open={cropperOpen}
            onClose={() => setCropperOpen(false)}
            imageSrc={cropperImageSrc}
            onConfirm={handleCropConfirmed}
          />

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4 mt-0">
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <History className="w-5 h-5 text-emerald-600" />
                سجل الحضور
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {attendanceHistory.map((att) => (
                  <div key={att.attendance_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{att.attendance_date}</p>
                      <p className="text-xs text-gray-500">
                        حضور: {att.check_in_time || '--:--'} | انصراف: {att.check_out_time || '--:--'}
                      </p>
                    </div>
                    <div className="text-end">
                      <p className="font-bold text-emerald-600">{att.work_hours || 0} ساعة</p>
                      {att.check_in_location_valid ? (
                        <CheckCircle className="w-4 h-4 text-green-500 inline" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 inline" />
                      )}
                    </div>
                  </div>
                ))}
                {attendanceHistory.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا يوجد سجل حضور</p>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
